import { config } from '../config';
import { read, TABLE } from '../data/db';
import { annotations, coachCards, hypotheses, matches, observations } from '../data/repos';
import { countReturnedSuggestions, validateCoachSuggestions } from '../domain/coach';
import type { CoachSuggestion } from '../domain/coach';
import { rendersOnCard } from '../domain/commands/coachCard';
import { byMatchHistory } from '../domain/file';
import { compareDiscovery } from '../domain/order';
import { ARTIFACT_PHASE } from '../types';
import type {
  AnnotationRow,
  CoachCardContext,
  CoachCardRow,
  HypothesisPhase,
  HypothesisRow,
  MatchFormat,
  MatchPurpose,
} from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { firstIncrements } from './discovery';
import { newId } from './ids';
import { log } from './logger';
import { loadSettings } from './settingsB';

/**
 * B spec §4.5 — **the coach facade** (build unit B-3, scenarios 22–32): what the adjustments
 * workflow reads and writes. The A convention, unchanged: `load*` is one readonly transaction;
 * each write dispatches exactly one command and returns `DispatchResult`. No `onCommitted` — a
 * workflow commit is a checkbox or a Save button, not a capture ack, and the screen re-reads on
 * success (the imperative re-read convention).
 *
 * The workflow's network half — `generateSuggestions` — landed with **B-15**, the unit that
 * builds the Step-2 payload; the Worker that answers it is **B-17's** (§11's dependency order).
 * Everything the durable-rows model needs is here: a killed generate costs one re-request
 * precisely because the draft below is the only persisted state, and nothing about a generate is
 * ever written until its suggestions commit.
 */

/** One Step-1 row: the pattern, and the annotation lines that ride into the payload with it. */
export interface WorkflowEntry {
  readonly hypothesis: HypothesisRow;
  /** Oldest first — the `annotations.listForMatch` order the detail sheet already shows. */
  readonly annotations: readonly AnnotationRow[];
}

/** What `loadWorkflowContext` returns — §4.5's three reads, in one transaction. */
export interface WorkflowContext {
  /**
   * Scenario 22 — every hypothesis with count > 0, in **DISC-4 order** (`cachedCount` desc,
   * ties: first observation asc, then `createdAt` asc): the engine's own `discoveryOrder`, so
   * this list and the capture surfaces can never disagree about a rank.
   */
  readonly entries: readonly WorkflowEntry[];
  /** The card already holding (match, context) — §2.5's *reopen* arm — or `undefined` to create. */
  readonly card: CoachCardRow | undefined;
  /**
   * The halftime card whatever the requested context — §2.5 step 4 shows it as reference
   * context at the top of the full-time flow. Identical to {@link card} when `context` is
   * `halftime`; read here rather than by a second transaction later.
   */
  readonly halftimeCard: CoachCardRow | undefined;
}

/**
 * Scenario 22's read: observed patterns (count > 0, DISC-4 order) with their annotations, plus
 * the existing card for this (match, context). One readonly transaction over the four tables —
 * `observations` because DISC-4's second term is the earliest **surviving** increment, derived
 * from the ledger through the same `firstIncrements` every other ranked load uses
 * (`loadDiscovery`, `loadHalftime`, `loadDistill`): one derivation, one call site per surface,
 * so this list and the capture surfaces can never disagree about a rank. Not a hot screen —
 * §5.1's "hot screens never subscribe to `observations`" is about the tap path, and this read
 * happens once, on workflow entry, at a break.
 */
export function loadWorkflowContext(
  matchId: string,
  context: CoachCardContext,
): Promise<WorkflowContext> {
  return read(
    [TABLE.hypotheses, TABLE.coachCards, TABLE.annotations, TABLE.observations],
    async () => {
      const [rows, cards, notes, ledger] = await Promise.all([
        hypotheses.listHypothesesForMatch(matchId),
        coachCards.listForMatch(matchId),
        annotations.listForMatch(matchId),
        observations.listObservationsForMatch(matchId),
      ]);
      const firstIncrementByHypothesis = firstIncrements(ledger);
      const byHypothesis = new Map<string, AnnotationRow[]>();
      for (const note of notes) {
        const bucket = byHypothesis.get(note.hypothesisId);
        if (bucket === undefined) byHypothesis.set(note.hypothesisId, [note]);
        else bucket.push(note);
      }
      return {
        entries: rows
          .filter((row) => row.cachedCount > 0)
          .map((hypothesis) => ({
            hypothesis,
            key: {
              id: hypothesis.id,
              cachedCount: hypothesis.cachedCount,
              firstObservationCreatedAt: firstIncrementByHypothesis.get(hypothesis.id) ?? null,
              createdAt: hypothesis.createdAt,
            },
          }))
          .sort((a, b) => compareDiscovery(a.key, b.key))
          .map(({ hypothesis }) => ({
            hypothesis,
            annotations: byHypothesis.get(hypothesis.id) ?? [],
          })),
        card: cards.find((card) => card.context === context),
        halftimeCard: cards.find((card) => card.context === ARTIFACT_PHASE.halftime),
      };
    },
    'loadWorkflowContext',
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// B-31 — what the break hub says about work that already exists (§21.3, BH-2)
// ─────────────────────────────────────────────────────────────────────────────

/** Whether this break holds a card at all, and if so whether it is finished. */
export type BreakCardStatus = 'none' | 'draft' | 'final';

/**
 * §21.3 — everything the hub's entry card needs to name its own state, and nothing else.
 *
 * The two counts are **different questions** and are not interchangeable. `accepted` is the
 * distinct persisted fact acceptance is (`AdjustmentStatus === 'accepted'`) — what a half-reviewed
 * draft has *decided* so far. `renderable` is the engine's own `rendersOnCard`, which is what a
 * finalized card actually shows, manual entries included. A draft that reported its manual entries
 * as "accepted" would be claiming decisions the user never made.
 */
export interface BreakCardState {
  readonly status: BreakCardStatus;
  readonly accepted: number;
  readonly renderable: number;
}

/**
 * §21.3 — the hub's read: **one readonly transaction over `coachCards` alone.**
 *
 * Deliberately *not* `loadWorkflowContext` (pre-dispositioned in the duplication ledger, §23.3):
 * that read opens four tables — hypotheses, annotations and the whole observation ledger — because
 * the workflow needs a ranked selection list. This one renders on **every break**, for both
 * purposes, before the user has decided whether they want the workflow at all. Same entity, a
 * different grain, and the grain is the point: an entry card must not cost a ledger scan.
 *
 * The defect this exists to fix is a *product* failure, not a storage one: on 2026-08-17 the rows
 * survived and the user still concluded his analysis was gone, because the entry was pure
 * create-copy. A silent survival is not a survival.
 *
 * @throws {StorageError} on a read failure.
 */
export function loadBreakCardState(
  matchId: string,
  context: CoachCardContext,
): Promise<BreakCardState> {
  return read(
    [TABLE.coachCards],
    async (): Promise<BreakCardState> => {
      const cards = await coachCards.listForMatch(matchId);
      const card = cards.find((row) => row.context === context);
      if (card === undefined) return { status: 'none', accepted: 0, renderable: 0 };
      return {
        status: card.status,
        accepted: card.adjustments.filter((entry) => entry.status === 'accepted').length,
        renderable: card.adjustments.filter(rendersOnCard).length,
      };
    },
    'loadBreakCardState',
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The card surfaces' read (B-16; §2.4's opponent file, scenarios 30 and 34)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One hypothesis as a finished card cites it — the "because —" line's whole content.
 *
 * The count travels with the text because it is *the evidence*: "fullbacks caught forward (5)"
 * says something "fullbacks caught forward" does not, and a card re-read weeks later has no
 * other way to recover how often it was actually seen.
 */
export interface CoachCardSource {
  readonly id: string;
  readonly text: string;
  readonly phase: HypothesisPhase;
  readonly count: number;
  /** The annotation lines for this pattern, oldest first (the `listForMatch` order). */
  readonly annotations: readonly string[];
}

/** One finished card, with everything a card surface needs to render it whole. */
export interface TeamCard {
  readonly card: CoachCardRow;
  /** The match's purpose — half of the (purpose, context) pair the framing derives from (§2.5). */
  readonly purpose: MatchPurpose | null;
  /** The match's calendar date (`YYYY-MM-DD`) — the card's "when" on a file spanning seasons. */
  readonly matchDate: string;
  /** Every pattern the match's cards cite, resolved. A card renders the ids its entries name. */
  readonly sources: readonly CoachCardSource[];
}

/**
 * The cited patterns, resolved from rows the caller already holds. Pure.
 *
 * Split out of {@link readCardSources} by **B-18**, whose match-record read has both tables open in
 * its own transaction and would otherwise have read them a second time — or, worse, restated this
 * eight-line join beside it. A card's "because —" line must mean the same thing on the opponent
 * file and on the match record; one mapper, two readers is what makes that structural.
 */
export function cardSourcesFrom(
  rows: readonly HypothesisRow[],
  notes: readonly AnnotationRow[],
  cited: ReadonlySet<string>,
): CoachCardSource[] {
  const byHypothesis = new Map<string, string[]>();
  for (const note of notes) {
    const bucket = byHypothesis.get(note.hypothesisId);
    if (bucket === undefined) byHypothesis.set(note.hypothesisId, [note.text]);
    else bucket.push(note.text);
  }
  return rows
    .filter((row) => cited.has(row.id))
    .map((row) => ({
      id: row.id,
      text: row.text,
      phase: row.phase,
      count: row.cachedCount,
      annotations: byHypothesis.get(row.id) ?? [],
    }));
}

/** The cited patterns of one match, with their annotation lines. Called inside the transaction. */
async function readCardSources(
  matchId: string,
  cited: ReadonlySet<string>,
): Promise<CoachCardSource[]> {
  const [rows, notes] = await Promise.all([
    hypotheses.listHypothesesForMatch(matchId),
    annotations.listForMatch(matchId),
  ]);
  return cardSourcesFrom(rows, notes, cited);
}

/**
 * Scenario 30/34 — every **finalized** card from every match against one opponent, newest match
 * first, each with the patterns it cites.
 *
 * ── Finalized only, and why that is not a filter of convenience ──────────────
 *
 * §2.5: *"Finalized cards are immutable and persist with the match (viewable from the match record
 * and the opponent file forever)."* A draft is unfinished business, not a record: it belongs to
 * whoever is still building it (the workflow) and to the Needs-finishing queue that chases it
 * (B-5's home), and it can still change under a reader's eyes. Surfacing one in a file that reads
 * as history would show a coach a half-written plan as though it were what they decided —
 * the same reason scenario 34's prep block admits finalized cards only.
 *
 * ── Order, and the read's shape ─────────────────────────────────────────────
 *
 * `byMatchHistory` (FILE-3's newest-first total order over the match date), then the repo's own
 * halftime-before-full-time order within a match — so the file reads as the season did. All of it
 * is **one readonly transaction**: a file that showed a card whose evidence came from a different
 * instant would be exactly the composed-copy defect this card's verification hunts for.
 * `hypotheses`/`annotations` are read **only for matches that hold a card**, so an opponent with a
 * long history and no cards costs two index reads.
 *
 * @throws {StorageError} on a read failure.
 */
export function loadTeamCards(teamId: string): Promise<TeamCard[]> {
  return read(
    [TABLE.matches, TABLE.coachCards, TABLE.hypotheses, TABLE.annotations],
    async (): Promise<TeamCard[]> => {
      const played = (await matches.listMatchesForTeam(teamId)).sort(byMatchHistory);
      const found: TeamCard[] = [];
      for (const match of played) {
        const finalized = (await coachCards.listForMatch(match.id)).filter(
          (row) => row.status === 'final',
        );
        if (finalized.length === 0) continue;
        const cited = new Set(
          finalized.flatMap((row) => row.adjustments.flatMap((entry) => entry.sourceHypothesisIds)),
        );
        const sources = cited.size === 0 ? [] : await readCardSources(match.id, cited);
        for (const card of finalized) {
          found.push({ card, purpose: match.purpose, matchDate: match.date, sources });
        }
      }
      return found;
    },
    'loadTeamCards',
  );
}

/**
 * §2.5 — the fresh draft for a break that holds no card yet: empty selection, empty
 * adjustments, id minted here (`§5.1`'s pre-built-row discipline — the bus mints no ids).
 * Pure construction; nothing is written until the first `upsertDraft`.
 */
export function newDraftRow(
  matchId: string,
  context: CoachCardContext,
  at: number = Date.now(),
): CoachCardRow {
  return {
    id: newId(),
    matchId,
    context,
    status: 'draft',
    selectedHypothesisIds: [],
    adjustments: [],
    generatedAt: null,
    finalizedAt: null,
    createdAt: at,
    updatedAt: at,
  };
}

/**
 * B §4.2 — commit the draft as it stands after one structural act (select toggle, accept,
 * reject, save-edit, add, remove). Interaction grain, whole row, no debounce anywhere (§2.5);
 * the handler preserves the stored `createdAt` and stamps `updatedAt` from the bus's clock.
 */
export function upsertDraft(row: CoachCardRow): Promise<DispatchResult> {
  return dispatch({ type: 'UpsertCoachCardDraft', matchId: row.matchId, row });
}

/** Scenario 29 — "Make the card (n)". Immutable from the moment this resolves ok. */
export function finalizeCard(matchId: string, cardId: string): Promise<DispatchResult> {
  return dispatch({ type: 'FinalizeCoachCard', matchId, cardId });
}

/** B §4.2 — drop the draft; the one-tap resolution F-1's completion guard points at (unit B-18). */
export function discardDraft(matchId: string, cardId: string): Promise<DispatchResult> {
  return dispatch({ type: 'DiscardCoachCardDraft', matchId, cardId });
}

// ─────────────────────────────────────────────────────────────────────────────
// The network half — §5.1's proxy, §5.2's request, §5.3's response, §5.4's taxonomy
// ─────────────────────────────────────────────────────────────────────────────

/**
 * §5.1 — the Worker route, **relative**, so the request is same-origin from B's installed PWA.
 *
 * The client holds no provider key (§5.1: the key is a Worker secret) and, deliberately, no
 * service origin either: a base URL compiled into the bundle is a deployment fact pretending to
 * be a constant.
 *
 * ── B-17's ruling: the path stays relative, and the Worker comes to it ──────
 *
 * B-15 left the question open. **B-17 ruled: keep this literal, and bind the existing Worker
 * onto B's own origin at `/v1/*` with a Cloudflare Worker route** (`ARCHITECTURE-B.md` § B-17
 * carries the deployment step and the tradeoff). The alternative — an absolute Worker URL
 * compiled in — was rejected because it is a CSP change, not a constant change: **B ships a
 * `'self'` policy**, so an absolute origin would need `connect-src` widened to a second host in
 * the shipped policy, and every future origin change would be a client rebuild rather than a DNS
 * edit. (Citation corrected 2026-08-19: this said *"ENV-4 pins B at `'self'`"*. Locked ENV-4 says
 * nothing about CSP — it permits and requires a backup service, and forbids telemetry, analytics,
 * error-reporting SaaS, third-party runtime scripts and multi-tenancy. The `'self'` policy is a
 * real property of the shipped bundle and a good one; it just is not ENV-4's doing, and the
 * ruling stands on the rebuild-vs-DNS argument regardless.)
 * What B-15 anticipated therefore holds unchanged: `ALLOWED_ORIGINS` still has to gain B's
 * origin, because a same-origin POST still carries an `Origin` header and the Worker's CORS gate
 * is fail-closed.
 *
 * If B is ever served somewhere a Worker route cannot reach — a bare `*.pages.dev` host has no
 * zone to attach one to — this one literal plus one `connect-src` entry is the whole of the
 * change, and the documented fallback says so.
 */
const COACH_PATH = '/v1/coach';

/**
 * §2.6 — the pre-flight route, derived from {@link COACH_PATH} rather than spelled again, so the
 * ruling above (relative path, Worker routed onto B's origin) cannot come to apply to one of the
 * two calls and not the other.
 */
const COACH_HEALTH_PATH = `${COACH_PATH}/health`;

const MODULE = 'app/coach';

/**
 * The stored Worker credential, trimmed, or `''` when there is none to send.
 *
 * **Blank is absent, and that is a rule rather than a tidy-up.** A settings row holding `"   "`
 * would otherwise become `Authorization: Bearer` with nothing after it — a request that reaches
 * the Worker, is refused there, and tells the user "the service refused the request" when the
 * truth is that nothing is set up. Both callers below take this same reading (B-19 extracted it
 * when the pre-flight chip became the second one), so the two can never disagree about what
 * "configured" means — which is precisely the disagreement §2.6 exists to make impossible.
 */
async function readCredential(): Promise<string> {
  const stored = (await loadSettings()).backupCredential;
  return typeof stored === 'string' ? stored.trim() : '';
}

/** §5.4's five codes. A closed union: every one has copy and a surface, or it is a defect. */
export type CoachFailureCode =
  | 'notConfigured'
  | 'unreachable'
  | 'refused'
  | 'serverError'
  | 'badResponse';

/** One selected pattern as §5.2 sends it — id round-trips so the answer can cite its sources. */
export interface CoachSelectionInput {
  readonly hypothesisId: string;
  readonly text: string;
  readonly phase: HypothesisPhase;
  readonly count: number;
  readonly annotations: readonly string[];
}

/**
 * §5.2's request body, whole. **What is absent is the contract** (scenario 24's negative): no
 * audio, no voice notes, no flags, no opponent player identities, no match notes, no set-piece
 * diagrams. This interface is where that negative becomes enforceable — a field that does not
 * exist cannot be sent by accident — and `workflowB.buildCoachRequestB` is its one constructor.
 *
 * **Five keys, exactly.** §18 replaced `roster: readonly CoachRosterInput[]` with `team: string |
 * null`, and **deleted `CoachRosterInput`**. That *narrows* the wire vocabulary rather than
 * widening it, so scenario 24's negative gets tighter: `roster`, `label` and `attributes` are now
 * keys no constructor can produce.
 *
 * The honest half, stated rather than glossed: folding the roster into one opaque `team` string
 * means the wire's team half is **unauditable by structural means** — `workflowB.test.ts`'s
 * substring scan audits *keys*, not content, and never was a property over user data. The
 * compensating control is the one that already exists: **one** constructor, composing `team` from
 * `domain/teamProfile.renderTeamProfile`'s output alone.
 */
export interface CoachRequest {
  readonly format: MatchFormat | null;
  readonly purpose: MatchPurpose | null;
  readonly context: CoachCardContext;
  readonly selections: readonly CoachSelectionInput[];
  /**
   * §18 — the team profile as **prose**, or `null` when none travels.
   *
   * `null` in exactly the two cases §18.5's disclosure calls `'withheld'` (scenario 24's rule) and
   * `'empty'` (no active players **and** no shape). That correspondence is the biconditional
   * `rosterDisclosureB` is tested against, not a coincidence of two functions agreeing today.
   */
  readonly team: string | null;
}

/**
 * What one generate attempt turned out to be.
 *
 * `cancelled` is a **third outcome, not a failure**: §2.5's boundary behavior aborts an in-flight
 * generate on any `Match.state` change, and the whole point is that no error reaches the capture
 * surface when the second half starts. Folding it into a failure code would put "Coach Claude
 * isn't reachable" on screen at the kickoff whistle.
 */
export type CoachOutcome =
  | {
      readonly kind: 'ok';
      readonly suggestions: readonly CoachSuggestion[];
      /**
       * B-30 / §20 — how many entries the model returned, **before** validation dropped or
       * truncated any. Carried so the review step can say *"Coach Claude returned 24 — the first
       * 20 are here"* rather than trimming the menu in silence; `truncationNoticeB` decides when
       * that sentence is true. It is always ≥ `suggestions.length`.
       */
      readonly returned: number;
    }
  | { readonly kind: 'failed'; readonly code: CoachFailureCode }
  | { readonly kind: 'cancelled' };

/**
 * §5.2/§5.3/§5.4 — POST the selection to the Worker and return what came back, validated.
 *
 * ── Detection, exactly as §5.4 tabulates it ─────────────────────────────────
 *
 * | Code | Detected by |
 * |---|---|
 * | `notConfigured` | no stored credential (`backupCredential` — §4.2's "one Worker, one credential") |
 * | `unreachable` | the fetch rejected (no signal, DNS, CORS), or the `COACH_TIMEOUT_MS` abort fired |
 * | `refused` | HTTP 401/403 |
 * | `serverError` | any other non-2xx (5xx, and a 404 from an endpoint not deployed yet), or a body that will not parse — §5.4's "malformed transport" |
 * | `badResponse` | parsed, but zero entries survived §5.3's validation |
 *
 * The mapping is **total**: every path out of this function is one of the five codes, `ok`, or
 * `cancelled`. There is no automatic retry — §5.4: an interactive, break-bounded flow retries by
 * the user's finger, once per press.
 *
 * ── The two aborts are different events ─────────────────────────────────────
 *
 * The caller's signal (a whistle, an unmount) means *stop, silently*; the internal timer means
 * *this is not going to answer*. One `AbortController` drives the fetch and a flag records which
 * fired, because an `AbortError` alone cannot tell them apart — and getting that backwards is
 * exactly the "an error rendered on the capture surface" outcome §2.5 rules out.
 *
 * @param request  built by `workflowB.buildCoachRequestB` — the one constructor for §5.2's body.
 * @param options  `signal` is the workflow's own controller, aborted on any `Match.state` change.
 */
export async function generateSuggestions(
  request: CoachRequest,
  options: { readonly signal?: AbortSignal } = {},
): Promise<CoachOutcome> {
  const credential = await readCredential();
  if (credential === '') {
    log.info(MODULE, 'a generate was declined: no service credential is stored', {
      cardContext: request.context,
    });
    return { kind: 'failed', code: 'notConfigured' };
  }

  const caller = options.signal;
  /**
   * Read through a function, never a narrowed local: `AbortSignal.aborted` flips *during* the
   * awaited fetch, and TypeScript's control-flow analysis would otherwise carry the value it
   * saw before the await into the catch — turning the whistle arm into unreachable code.
   */
  const callerAborted = (): boolean => options.signal?.aborted === true;
  if (callerAborted()) return { kind: 'cancelled' };

  const controller = new AbortController();
  let timedOut = false;
  const abortForCaller = (): void => {
    controller.abort();
  };
  caller?.addEventListener('abort', abortForCaller);
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, config.COACH_TIMEOUT_MS);

  const startedAt = Date.now();
  try {
    const response = await fetch(COACH_PATH, {
      method: 'POST',
      headers: {
        // The credential travels as a header, never a query parameter — a URL is logged by
        // every hop that touches it, and §5.1's Worker logs status and size only.
        authorization: `Bearer ${credential}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(request),
      cache: 'no-store',
      signal: controller.signal,
    });

    if (response.status === 401 || response.status === 403) {
      log.warn(MODULE, 'the coach service refused the request', { status: response.status });
      return { kind: 'failed', code: 'refused' };
    }
    if (!response.ok) {
      log.warn(MODULE, 'the coach service answered with an error status', {
        status: response.status,
      });
      return { kind: 'failed', code: 'serverError' };
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch (cause) {
      // §5.4's "malformed transport": a 200 whose body is not JSON is a broken hop rather than a
      // model problem — the same sentence and the same single Retry serve both.
      log.warn(MODULE, 'the coach response body could not be parsed', {
        message: cause instanceof Error ? cause.message : String(cause),
      });
      return { kind: 'failed', code: 'serverError' };
    }

    const suggestions = validateCoachSuggestions(
      payload,
      request.selections.map((selection) => selection.hypothesisId),
      {
        maxSuggestions: config.COACH_MAX_SUGGESTIONS,
        maxChars: config.COACH_ADJUSTMENT_MAX_CHARS,
      },
    );
    if (suggestions.length === 0) {
      log.warn(MODULE, 'the coach response held no usable suggestion', {
        elapsedMs: Date.now() - startedAt,
      });
      return { kind: 'failed', code: 'badResponse' };
    }

    const returned = countReturnedSuggestions(payload);
    log.info(MODULE, 'coach suggestions received', {
      count: suggestions.length,
      returned,
      elapsedMs: Date.now() - startedAt,
      cardContext: request.context,
    });
    return { kind: 'ok', suggestions, returned };
  } catch (cause) {
    // The caller's abort wins the tie: a whistle landing in the same tick as the timeout is
    // still a whistle, and §2.5 forbids an error surfacing from it.
    if (callerAborted()) return { kind: 'cancelled' };
    if (timedOut) {
      log.warn(MODULE, 'the coach request timed out', { timeoutMs: config.COACH_TIMEOUT_MS });
      return { kind: 'failed', code: 'unreachable' };
    }
    log.warn(MODULE, 'the coach request could not be made', {
      message: cause instanceof Error ? cause.message : String(cause),
    });
    return { kind: 'failed', code: 'unreachable' };
  } finally {
    clearTimeout(timer);
    caller?.removeEventListener('abort', abortForCaller);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// The pre-flight — §2.6, and the only reader of B-17's `GET /v1/coach/health`
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What one *Test connection* press turned out to be.
 *
 * **Six outcomes, because six things can actually be wrong, and the chip's whole job is to say
 * WHICH.** Pre-mortem incident 1 is the reason this type is not three-valued: *"a config failure
 * worded as a network failure is how the flagship feature ships dead"* — a coach standing at a
 * pitch told "no signal" will walk toward the road, and no amount of walking fixes an unset
 * Worker secret.
 *
 * The two "not configured" outcomes are **different sides of the same seam** and are deliberately
 * not collapsed:
 *
 * | Outcome | What is true | Where the fix is |
 * |---|---|---|
 * | `notConfigured` | no credential is stored on THIS device | the field directly above the chip |
 * | `workerUnconfigured` | the Worker answered, this credential is good, and the Worker holds no provider key | the Worker's own secret binding, not the phone |
 *
 * `workerUnconfigured` is reported by the endpoint as `coach: 'unconfigured'` — a fact about the
 * deployment rather than key material (`service/src/coach.ts`), reachable only by a caller who
 * already authenticated.
 */
export type CoachPreflightOutcome =
  | { readonly kind: 'ready' }
  | { readonly kind: 'notConfigured' }
  | { readonly kind: 'workerUnconfigured' }
  | { readonly kind: 'refused' }
  | { readonly kind: 'serverError'; readonly status: number | null }
  | { readonly kind: 'unreachable' };

/**
 * §2.6 — exercise DNS, CORS, authentication and Worker liveness **without a model call**.
 *
 * ── Every arm reports the branch actually taken ─────────────────────────────
 *
 * Nothing here is inferred from configuration. `ready` is returned only when a real response
 * carried `coach: 'ready'`; a stored credential on its own never produces it, and neither does a
 * 200 whose body says something else. That is the discipline the chip exists to embody — a chip
 * that said "connected" because a setting exists would be a more convincing version of the
 * failure it was built to catch.
 *
 * | Outcome | Detected by |
 * |---|---|
 * | `notConfigured` | no stored credential — no request is made at all |
 * | `ready` | 2xx, JSON, `coach: 'ready'` |
 * | `workerUnconfigured` | 2xx, JSON, `coach: 'unconfigured'` |
 * | `refused` | HTTP 401/403 |
 * | `serverError` | any other non-2xx, an unparseable body, or a body whose `coach` is neither value |
 * | `unreachable` | the fetch rejected (no signal, DNS, CORS), or the `COACH_PREFLIGHT_TIMEOUT_MS` abort fired |
 *
 * ── Bounded by its own, much shorter, clock ─────────────────────────────────
 *
 * `COACH_PREFLIGHT_TIMEOUT_MS` (8 s) rather than `COACH_TIMEOUT_MS` (45 s), and the relation is
 * held by config validation. A pre-flight is pressed while the user watches it; 45 s of spinner
 * on a control whose entire purpose is a fast answer would be indistinguishable from the hang it
 * is meant to diagnose.
 *
 * There is **no retry** and no automatic polling: like §5.4's generate, this retries by the
 * user's finger, once per press.
 */
export async function preflight(): Promise<CoachPreflightOutcome> {
  const credential = await readCredential();
  if (credential === '') {
    log.info(MODULE, 'a pre-flight was declined: no service credential is stored');
    return { kind: 'notConfigured' };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, config.COACH_PREFLIGHT_TIMEOUT_MS);
  const startedAt = Date.now();

  try {
    const response = await fetch(COACH_HEALTH_PATH, {
      method: 'GET',
      // A header, never a query parameter: a URL is logged by every hop that touches it.
      headers: { authorization: `Bearer ${credential}` },
      cache: 'no-store',
      signal: controller.signal,
    });

    if (response.status === 401 || response.status === 403) {
      log.warn(MODULE, 'the pre-flight was refused', { status: response.status });
      return { kind: 'refused' };
    }
    if (!response.ok) {
      log.warn(MODULE, 'the pre-flight answered with an error status', {
        status: response.status,
      });
      return { kind: 'serverError', status: response.status };
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch (cause) {
      log.warn(MODULE, 'the pre-flight body could not be parsed', {
        message: cause instanceof Error ? cause.message : String(cause),
      });
      return { kind: 'serverError', status: response.status };
    }

    const coach = (payload as { coach?: unknown } | null)?.coach;
    if (coach === 'ready') {
      log.info(MODULE, 'pre-flight: the coach service is ready', {
        elapsedMs: Date.now() - startedAt,
      });
      return { kind: 'ready' };
    }
    if (coach === 'unconfigured') {
      // Not an error status, and that is the point: the hop is healthy and the credential is
      // good. Logged at warn because it means the feature cannot work until a deploy changes.
      log.warn(MODULE, 'pre-flight: the coach service holds no provider key', {
        elapsedMs: Date.now() - startedAt,
      });
      return { kind: 'workerUnconfigured' };
    }

    log.warn(MODULE, 'the pre-flight answered something this app cannot read', {
      status: response.status,
    });
    return { kind: 'serverError', status: response.status };
  } catch (cause) {
    // One abort source here (the timer), so there is nothing to tell apart: a timed-out
    // pre-flight and a rejected fetch are the same sentence to the user — the hop did not answer.
    log.warn(MODULE, 'the pre-flight could not be made', {
      message: cause instanceof Error ? cause.message : String(cause),
      timeoutMs: config.COACH_PREFLIGHT_TIMEOUT_MS,
    });
    return { kind: 'unreachable' };
  } finally {
    clearTimeout(timer);
  }
}
